import { z } from "zod";
var nullableStringSchema = z.string().nullable();
var descriptionSchema = z.string().nullable().transform(function (value) { return value || ""; });
var nullableNumberSchema = z.coerce.number().nullable();
var availabilityFlagSchema = z.union([z.boolean(), z.coerce.number()]);
var priceSchema = z.union([z.number(), z.string()]);
var priceLevelsSchema = z.union([z.record(z.string(), priceSchema), z.string()]);
var timestampSchema = z.string().nullable().optional();
export var specialListItemSchema = z
    .object({
    id: z.coerce.number(),
    first_page: nullableNumberSchema,
    store_id: z.coerce.number(),
    name: z.string(),
    description: descriptionSchema,
    display_in_specials: availabilityFlagSchema,
    enabled: availabilityFlagSchema,
    imageURL: nullableStringSchema,
    start_time: nullableStringSchema,
    end_time: nullableStringSchema,
    available_days: nullableStringSchema,
    sort_order: nullableNumberSchema,
    catering_menu_availability: z.enum(["not_catering", "catering", "both"]).default("not_catering"),
    created_at: timestampSchema,
    updated_at: timestampSchema,
})
    .passthrough();
export var specialModifierSelectionSchema = z
    .object({
    id: z.coerce.number(),
    quantity: z.coerce.number(),
    portion: z.string().optional(),
    size: z.string().optional(),
})
    .passthrough();
export var specialAddOnSelectionSchema = z
    .object({
    id: z.coerce.number(),
    quantity: z.coerce.number(),
})
    .passthrough();
export var specialItemChoiceDetailsSchema = z
    .object({
    item: z.coerce.number().optional(),
    category: z.string().optional(),
    includedModifiers: z.record(z.string(), z.unknown()).optional(),
    modifiers: z.record(z.string(), z.unknown()).optional(),
    addOns: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
export var specialSelectedChoiceSchema = z
    .object({
    item: z.coerce.number(),
    quantity: z.coerce.number(),
    modifiers: z.array(specialModifierSelectionSchema),
    addOns: z.array(specialAddOnSelectionSchema),
    name: z.string(),
    itemNavigation: z.coerce.number(),
    choices: specialItemChoiceDetailsSchema,
    comment: z.string(),
})
    .passthrough();
export var specialSubmitPayloadSchema = z.object({
    special_id: z.coerce.number(),
    pages: z.record(z.string(), z.array(specialSelectedChoiceSchema)),
});
export var specialItemGroupOptionSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string(),
    menu_level: z.coerce.number(),
    is_matrix: availabilityFlagSchema,
    price: priceSchema,
    matrix_price: z.union([z.record(z.string(), priceSchema), z.string()]).nullable(),
    is_core: availabilityFlagSchema,
})
    .passthrough();
export var specialItemGroupSchema = z
    .object({
    id: z.coerce.number().optional(),
    name: z.string(),
    items: z.array(specialItemGroupOptionSchema),
})
    .passthrough();
export var specialModificationSchema = z
    .object({
    id: z.coerce.number(),
    modification_group_id: z.coerce.number(),
    name: z.string(),
    pos_name: nullableStringSchema,
    sort_order: nullableNumberSchema,
    level_price: priceLevelsSchema,
    maximum_quantity: z.coerce.number(),
    included_quantity: z.coerce.number(),
    selected_quantity: z.coerce.number(),
    show_half_toppings: availabilityFlagSchema,
    show_double_toppings: availabilityFlagSchema,
    matrix_weight: z.coerce.number(),
    status: availabilityFlagSchema,
})
    .passthrough();
export var specialModificationGroupSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string(),
    max_selection: z.coerce.number(),
    sort_order: nullableNumberSchema,
    control_type: z.string(),
    option_type: z.string(),
    status: availabilityFlagSchema,
    modifications: z.array(specialModificationSchema),
})
    .passthrough();
export var specialCategoryReferenceSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string(),
})
    .passthrough();
export var specialAddOnSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string(),
    price: priceSchema,
    is_core: availabilityFlagSchema,
})
    .passthrough();
export var specialAddOnGroupSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string(),
    addons: z.array(specialAddOnSchema),
})
    .passthrough();
export var specialItemNavigationSchema = z
    .object({
    id: z.coerce.number(),
    name: z.string(),
    pos_name: nullableStringSchema,
    description: descriptionSchema,
    status: availabilityFlagSchema,
    sort_order: nullableNumberSchema,
    imageURL: nullableStringSchema,
    imageURL_banner: nullableStringSchema,
    store_id: z.coerce.number(),
    item_group_id: z.coerce.number(),
    pos_id: z.coerce.number(),
    menu_id: nullableNumberSchema,
    max_toppings: z.coerce.number(),
    matrix_weight: z.coerce.number(),
    level_price: priceLevelsSchema,
    print_in_order: availabilityFlagSchema,
    modification_groups: z.array(specialModificationGroupSchema),
    item_group: specialItemGroupSchema,
    categories: z.array(specialCategoryReferenceSchema),
    addon_groups: z.array(specialAddOnGroupSchema),
    is_core: availabilityFlagSchema,
})
    .passthrough();
export var specialPageSchema = z
    .object({
    id: z.coerce.number(),
    next_page: nullableNumberSchema,
    special_id: z.coerce.number(),
    name: z.string(),
    minimum_required: z.coerce.number(),
    maximum_allowed: z.coerce.number(),
    item_navs: z.array(specialItemNavigationSchema),
    created_at: timestampSchema,
    updated_at: timestampSchema,
})
    .passthrough();
export var specialPagesByIdSchema = z.record(z.string(), specialPageSchema);
export var specialListResponseSchema = z.array(specialListItemSchema);
export var specialFlowResponseSchema = specialListItemSchema.extend({
    pages: specialPagesByIdSchema,
});
export var specialValidationErrorSchema = z
    .object({
    type: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    message: z.string().optional(),
    errors: z.record(z.string(), z.array(z.string())).optional(),
})
    .passthrough();
